import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Code, Terminal, Database, Layout, Server,
  Cpu, Globe, Zap, Briefcase, Users,
  Target, Lightbulb, Compass, MapPin, Award, Cloud, Loader2
} from 'lucide-react';
import OrbitSystem from '../components/OrbitSystem';
import ResumeUploadRitual from '../components/ResumeUploadRitual';
import StatCardTransition from '../components/wrapped/StatCardTransition';
import TransitionSwoop from '../components/wrapped/TransitionSwoop';
import ResumeProcessingAnimation from '../components/wrapped/ResumeProcessingAnimation';
import './OnboardingRings.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

// Premium "Solar System" Onboarding
export default function OnboardingRings() {
  const { user, hasDoneOnboarding, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const scrapedProfile = location.state?.scrapedProfile;
  
  // --- State ---
  const [step, setStep] = useState('upload'); // 'upload' | 'processing' | 'questions'

  const [outerIcons, setOuterIcons] = useState([
    { id: 1, label: 'Python', icon: Code, color: 'text-blue-500', bg: 'bg-blue-50' },
    { id: 2, label: 'React', icon: Layout, color: 'text-cyan-500', bg: 'bg-cyan-50' },
    { id: 3, label: 'AWS', icon: Cloud, color: 'text-orange-500', bg: 'bg-orange-50' },
    { id: 4, label: 'Node', icon: Server, color: 'text-green-500', bg: 'bg-green-50' },
    { id: 5, label: 'Design', icon: Globe, color: 'text-pink-500', bg: 'bg-pink-50' },
    { id: 6, label: 'System', icon: Cpu, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  ]);

  const [innerIcons, setInnerIcons] = useState([
    { id: 101, label: 'Leadership', icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
    { id: 102, label: 'Product', icon: Zap, color: 'text-amber-500', bg: 'bg-amber-50' },
    { id: 103, label: 'Strategy', icon: Target, color: 'text-red-500', bg: 'bg-red-50' },
    { id: 104, label: 'Vision', icon: Lightbulb, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  ]);

  const [candidateName, setCandidateName] = useState('');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [showSwoop, setShowSwoop] = useState(false);
  const [uploadStuck, setUploadStuck] = useState(false);
  const uploadTimeoutRef = useRef(null);
  const uploadAdTimeoutRef = useRef(null);
  const uploadStartedAtRef = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(uploadTimeoutRef.current);
      clearTimeout(uploadAdTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/connect-email');
    }
  }, [user, loading, navigate]);
  
  useEffect(() => {
    if (hasDoneOnboarding && !location.state?.fromGmailSync) {
         navigate('/roadmap');
    }
  }, [hasDoneOnboarding, location.state, navigate]);

  useEffect(() => {
    // Keep listener for dev/testing or if event is dispatched elsewhere
    function handleResume(e) {
      processResumeData(e?.detail || {});
      setStep('questions');
    }

    window.addEventListener('resume:uploaded', handleResume);
    return () => window.removeEventListener('resume:uploaded', handleResume);
  }, []);

  if (hasDoneOnboarding && !location.state?.fromGmailSync) {
     return (
        <div className="flex items-center justify-center h-screen bg-slate-50">
           <div className="text-center">
             <h2 className="text-2xl font-bold mb-2">Welcome back!</h2>
             <p>Taking you to your career map...</p>
           </div>
        </div>
     );
  }

  const questions = [
    'What parts of your previous jobs did you hate and do you not want to do as much of in your future roles?',
    'What are your dream jobs?',
    'What are your dream career goals? (Ex. Becoming a thought leader, reaching 1M followers on insta, be a driver and original voice in the cultural conversation, be recognized as an influential figure and leader in X industry, be part of a drama, voice act, etc.)',
    'Who are your career role models?',
    'What is your country of residence?'
  ];

  // --- Helpers ---
  
  function getIconForLabel(label) {
    const l = label.toLowerCase();
    if (l.includes('python') || l.includes('code')) return Code;
    if (l.includes('react') || l.includes('front')) return Layout;
    if (l.includes('aws') || l.includes('cloud')) return Cloud;
    if (l.includes('node') || l.includes('back')) return Server;
    if (l.includes('design') || l.includes('ui')) return Globe;
    if (l.includes('lead') || l.includes('manage')) return Users;
    if (l.includes('product')) return Zap;
    if (l.includes('strat')) return Target;
    if (l.includes('vision') || l.includes('idea')) return Lightbulb;
    if (l.includes('loc') || l.includes('remote')) return MapPin;
    return Briefcase;
  }

  function getRandomStyle() {
    const styles = [
      { color: 'text-blue-500', bg: 'bg-blue-50' },
      { color: 'text-cyan-500', bg: 'bg-cyan-50' },
      { color: 'text-orange-500', bg: 'bg-orange-50' },
      { color: 'text-green-500', bg: 'bg-green-50' },
      { color: 'text-pink-500', bg: 'bg-pink-50' },
      { color: 'text-purple-600', bg: 'bg-purple-50' },
      { color: 'text-indigo-500', bg: 'bg-indigo-50' },
      { color: 'text-rose-500', bg: 'bg-rose-50' },
    ];
    return styles[Math.floor(Math.random() * styles.length)];
  }

  // --- Semantic Processing ---

  function processInputToNodes(input, question) {
    if (!input || !input.trim()) return [];

    const isNegative = question.toLowerCase().includes('hate') || 
                       question.toLowerCase().includes('dislike') || 
                       question.toLowerCase().includes('avoid');

    // Split by common delimiters
    const chunks = input.split(/,|&|\band\b|\/|\n/).map(c => c.trim()).filter(c => c.length > 0);

    return chunks.map(chunk => {
      // Normalize: Capitalize first letter
      let label = chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase();
      
      // Remove filler words if they start the phrase
      label = label.replace(/^(A|The|An)\s+/i, '');

      // Apply semantic rules
      if (isNegative) {
        if (!label.startsWith('Not ') && !label.startsWith('Less ')) {
          label = `Not ${label}`;
        }
      } else {
        // Positive context - maybe add "More" if it's a value? 
        // For now, keep it clean as requested ("Value [concept]" or just concept)
        // The prompt says: "If the question asks what the user LIKES: Keep positive form"
      }

      // Truncate if too long for a pill
      if (label.length > 16) {
        label = label.substring(0, 14) + '..';
      }

      return {
        label,
        raw: chunk
      };
    });
  }

  // --- Effects ---

  function processResumeData(detail) {
    setResumeData(detail); // Store full resume data for generation
    
    // Extract skills from all possible fields
    const skills = detail.all_skills || detail.skills || detail.tech || [];
    const soft = detail.soft_skills || detail.softSkills || detail.soft || [];
    
    if (detail.name || detail.full_name) setCandidateName(detail.name || detail.full_name);

    if (Array.isArray(skills) && skills.length) {
      const mapped = skills.slice(0, 8).map((s, i) => {
        const style = getRandomStyle();
        return { 
          id: Date.now() + i, 
          label: s,
          icon: getIconForLabel(s),
          ...style
        };
      });
      setOuterIcons(mapped);
    }

    if (Array.isArray(soft) && soft.length) {
      const mapped = soft.slice(0, 6).map((s, i) => {
        const style = getRandomStyle();
        return { 
          id: Date.now() + i + 1000, 
          label: s,
          icon: getIconForLabel(s),
          ...style
        };
      });
      setInnerIcons(mapped);
    }
  }

  async function generateCareerPaths(finalAnswers) {
    setIsGenerating(true);
    try {
      // Construct conversation object
      const conversation = questions.map((q, i) => ({
        question: q,
        answer: finalAnswers[i] || ''
      }));

      const conversationContext = {
        resume_data: resumeData || {},
        conversation: conversation,
        selected_tags: [...outerIcons, ...innerIcons].map(i => i.label),
        country: finalAnswers[4] || 'United States', // Last question is country
      };

      const response = await fetch(`${API_BASE}/api/generate-career-path`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(conversationContext),
      });

      if (!response.ok) throw new Error('Failed to generate paths');
      
      const data = await response.json();
      
      // Supabase is the single source of truth
      // localStorage.setItem('careerFormData', JSON.stringify(conversationContext));
      // localStorage.setItem('savedCareerPaths', JSON.stringify(data));
      // localStorage.setItem('onboardingComplete', 'true');

      // Save roadmap to Supabase
      try {
        const saveResponse = await fetch(`${API_BASE}/api/save-roadmap`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: `Career Roadmap - ${new Date().toLocaleDateString()}`,
            form_data: conversationContext,
            career_paths: data,
            role_model_research: null,
            non_linear_research: null
          })
        });

        if (saveResponse.ok) {
          const saveData = await saveResponse.json();
          const roadmapId = saveData.data?.id || saveData.roadmap_id;
          
          if (roadmapId) {
            // localStorage.setItem('currentRoadmapId', roadmapId);
            toast.success('Your constellation is ready!');
            navigate(`/roadmap/${roadmapId}`);
            return;
          }
        }
      } catch (error) {
        console.warn('⚠️ Error saving roadmap to Supabase:', error);
      }

      // Fallback navigation if save fails or no ID returned
      toast.success('Your constellation is ready!');
      navigate('/roadmap');

    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate career paths. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }

  function submitAnswer() {
    const currentQ = questions[questionIndex];
    const processedNodes = processInputToNodes(inputValue, currentQ);
    
    // Allow empty input if user wants to skip, but maybe enforce at least one?
    // For now, let's allow it but not add nodes if empty.
    
    const newAnswers = [...answers, inputValue];
    setAnswers(newAnswers);
    setInputValue('');
    
    // Add processed nodes to rings
    if (processedNodes.length) {
      processedNodes.forEach((node, idx) => {
        const style = getRandomStyle();
        const newItem = { 
          id: Date.now() + idx, 
          label: node.label, 
          icon: getIconForLabel(node.raw),
          ...style
        };
        
        // Distribute between rings
        if (idx % 2 === 0) {
          setOuterIcons(prev => [...prev, newItem]);
        } else {
          setInnerIcons(prev => [...prev, newItem]);
        }
      });
    }

    if (questionIndex < questions.length - 1) {
      setQuestionIndex(i => i + 1);
    } else {
      // Finished!
      generateCareerPaths(newAnswers);
    }
  }

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-between relative overflow-hidden font-sans text-slate-900 selection:bg-blue-100"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/bluewhite.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        fontFamily: "'Montserrat', sans-serif"
      }}
    >
      <TransitionSwoop isVisible={showSwoop} onComplete={() => setShowSwoop(false)} />
      
      <AnimatePresence mode="wait">
        {step === 'upload' && !isUploading && (
          <motion.div
            key="upload"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="flex-1 w-full flex flex-col items-center justify-center p-6 pt-24"
          >
            <div className="max-w-4xl w-full text-center mb-6">
              <h1 className="text-4xl md:text-6xl font-black text-black mb-6 tracking-tighter leading-tight">
                Let's check out what's going on with your life...
              </h1>
              <p className="text-xl text-black font-bold mb-3">
                a super short tutorial on how to fetch your youtube watch history
              </p>
              <div className="text-base text-gray-600 space-y-1 mb-4">
                <p>
                  <span className="font-semibold">1.</span>{' '}
                  <a
                    href="https://www.youtube.com/feed/history"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    Open your YouTube watch history
                  </a>
                </p>
                <p><span className="font-semibold">2.</span> Click <span className="font-medium">File</span> in your browser menu bar → <span className="font-medium">Print</span></p>
                <p><span className="font-semibold">3.</span> Save as PDF and upload it here</p>
              </div>
            </div>
            <ResumeUploadRitual
              onUploadStart={() => {
                setIsUploading(true);
                setUploadStuck(false);
                uploadStartedAtRef.current = Date.now();
                window.dispatchEvent(new CustomEvent('fig:hide-upload-ad'));
                // Show "stuck?" escape hatch after 30s
                uploadTimeoutRef.current = setTimeout(() => setUploadStuck(true), 30000);
                uploadAdTimeoutRef.current = setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('fig:show-upload-ad'));
                }, 1000);
              }}
              onUploadError={(err) => {
                clearTimeout(uploadTimeoutRef.current);
                clearTimeout(uploadAdTimeoutRef.current);
                setIsUploading(false);
                setUploadStuck(false);
                uploadStartedAtRef.current = null;
                window.dispatchEvent(new CustomEvent('fig:hide-upload-ad'));
                console.error("Upload error caught in parent:", err);
              }}
              onUploadComplete={(data) => {
                clearTimeout(uploadTimeoutRef.current);
                processResumeData(data);
                const uploadStartedAt = uploadStartedAtRef.current || Date.now();
                const elapsedMs = Date.now() - uploadStartedAt;
                const remainingTransitionMs = Math.max(0, 20000 - elapsedMs);
                setTimeout(() => {
                    navigate('/fig-chat', {
                      state: {
                        scrapedProfile: scrapedProfile,
                        fromOnboarding: true,
                        resumeData: data
                      }
                    });
                    uploadStartedAtRef.current = null;
                }, remainingTransitionMs);
              }}
            />
          </motion.div>
        )}

        {(step === 'processing' || isUploading) && (
          <motion.div
            key="processing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center min-h-screen"
          >
            <ResumeProcessingAnimation isActive={true} />
            {uploadStuck && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 flex flex-col items-center gap-3"
              >
                <p className="text-sm text-slate-600 font-medium">Taking longer than expected…</p>
                <button
                  onClick={() => {
                    clearTimeout(uploadTimeoutRef.current);
                    navigate('/fig-chat', {
                      state: {
                        scrapedProfile: scrapedProfile,
                        fromOnboarding: true,
                        resumeData: resumeData
                      }
                    });
                  }}
                  className="px-6 py-2.5 bg-black text-white rounded-full text-sm font-bold hover:bg-slate-800 transition-all"
                >
                  Continue anyway →
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {step === 'questions' && (
          <motion.div 
            key="questions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="flex-1 w-full flex flex-col items-center justify-between h-full"
          >
            {/* --- Top Zone: Orbit System --- */}
            <div className="flex-1 w-full flex items-center justify-center relative pt-12 pb-8">
              <OrbitSystem 
                candidateName={candidateName || user?.user_metadata?.full_name}
                innerIcons={innerIcons}
                outerIcons={outerIcons}
              />
            </div>

            {/* --- Bottom Zone: Question Card --- */}
            <div className="w-full max-w-2xl px-6 pb-12 md:pb-16 z-20">
              <StatCardTransition delay={0.3} className="bg-white/80 backdrop-blur-xl border border-white/60 !shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xs font-bold tracking-wider text-slate-400 uppercase">Question {questionIndex + 1} of {questions.length}</span>
                  <div className="flex gap-1">
                    {questions.map((_, idx) => (
                      <div key={idx} className={`h-1.5 rounded-full transition-all duration-500 ${idx === questionIndex ? 'w-8 bg-slate-800' : 'w-2 bg-slate-200'}`} />
                    ))}
                  </div>
                </div>
                
                <h2 className="text-xl md:text-4xl font-black text-slate-900 mb-6 leading-tight tracking-tighter">
                  {questions[questionIndex]}
                </h2>

                <div className="flex gap-3">
                  <input
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !isGenerating && submitAnswer()}
                    placeholder="Type your answer..."
                    disabled={isGenerating}
                    className="flex-1 bg-slate-50 border border-slate-200 text-slate-800 text-lg rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-all placeholder:text-slate-400 disabled:opacity-50 font-medium"
                    autoFocus
                  />
                  <button 
                    onClick={submitAnswer}
                    disabled={isGenerating}
                    className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-slate-900/10 flex items-center gap-2 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <span>Next</span>
                    )}
                  </button>
                </div>

                {/* Answer Pills */}
                <div className="flex flex-wrap gap-2 mt-6 min-h-[2rem]">
                  {answers.map((a, idx) => (
                    <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-slate-100 text-slate-600 border border-slate-200">
                      {a}
                    </span>
                  ))}
                </div>
              </StatCardTransition>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
